import { FC, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Box, Divider } from '@mui/material';
import { CustomOutputFieldsType } from 'entities/Extensions/types';
import { Paper } from 'shared/ui/Paper';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Switch } from 'shared/ui/Switch';
import { IconButton } from 'shared/ui/IconButton';

enum FieldsSettingsEnum {
	isEnabled = 'isEnabled',
	addMissingLabels = 'addMissingLabels',
}

type OptionsType = {
	setting: FieldsSettingsEnum;
	descriptionText: string;
	isActive: boolean;
};

type MergeRowPropsType = {
	isEnabled: boolean;
	onToggle: () => void;
	onNavigate: () => void;
};

type SettingsTablePropsType = {
	fieldsStateData: CustomOutputFieldsType;
	setFieldsState: (data: CustomOutputFieldsType) => void;
	mergeRow?: MergeRowPropsType;
};

const SettingsTable: FC<SettingsTablePropsType> = ({ fieldsStateData, setFieldsState, mergeRow }) => {
	const { t } = useTranslation();

	const renderOptions: OptionsType[] = useMemo(
		() => [
			{
				setting: FieldsSettingsEnum.isEnabled,
				descriptionText: t('extensions.ex_list.output_file.settings_description_1'),
				isActive: fieldsStateData.isEnabled,
			},
			{
				setting: FieldsSettingsEnum.addMissingLabels,
				descriptionText: t('extensions.ex_list.output_file.settings_description_2'),
				isActive: fieldsStateData.addMissingLabels,
			},
		],
		[fieldsStateData, t],
	);

	const handleToggleSetting = (item: OptionsType) => {
		setFieldsState({
			...fieldsStateData,
			[item.setting]: !fieldsStateData[item.setting],
		});
	};

	return (
		<Paper sx={{ width: '100%', padding: 4 }}>
			{renderOptions.map((item, index) => (
				<Fragment key={item.setting}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 4,
						}}
					>
						<Switch checked={item.isActive} onChange={() => handleToggleSetting(item)} />
						<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
							{item.descriptionText}
						</Typography>
					</Box>

					{(index < renderOptions.length - 1 || mergeRow) && (
						<Divider
							color={ColorPalette.periwinkle}
							sx={{ marginY: 2, borderColor: 'transparent' }}
						/>
					)}
				</Fragment>
			))}

			{mergeRow && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Switch checked={mergeRow.isEnabled} onChange={mergeRow.onToggle} />
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('extensions.ex_list.output_file.merge_settings_description')}
					</Typography>
					<IconButton iconId={'expand_right'} onClick={mergeRow.onNavigate} />
				</Box>
			)}
		</Paper>
	);
};

export default SettingsTable;
